import {
  ComponentProps,
  Fragment,
  ReactNode,
  forwardRef,
  isValidElement,
} from 'react';

import classNames from 'classnames';

import {
  DialogAlert,
  DialogConfirmation,
  DialogFullScreen,
} from '@spotify-internal/encore-web';

import { useDialogSlots } from './composition';
import {
  DialogMaxWidth,
  DialogShellProps,
  DialogVariant,
  useDialogContext,
} from './context';

import styles from './DialogComposite.module.css';

export type MoleculeProps = ComponentProps<typeof DialogConfirmation> & {
  variant?: DialogVariant;
  onClose: () => void;
};

const MAX_WIDTH_CLASS_NAMES: Record<Exclude<DialogMaxWidth, false>, string> = {
  xs: styles.maxWidthXs,
  sm: styles.maxWidthSm,
  md: styles.maxWidthMd,
  lg: styles.maxWidthLg,
  xl: styles.maxWidthXl,
};

const getWidthClassName = ({
  maxWidth,
  fullWidth,
}: DialogShellProps): string | undefined =>
  classNames(
    maxWidth !== undefined && styles.maxWidth,
    maxWidth && MAX_WIDTH_CLASS_NAMES[maxWidth],
    fullWidth && styles.fullWidth,
  ) || undefined;

const joinNodes = (nodes: ReactNode[]): ReactNode =>
  nodes.length > 0 && (
    <>
      {nodes.map((node, index) => (
        <Fragment key={index}>{node}</Fragment>
      ))}
    </>
  );

export const Molecule = forwardRef<HTMLElement, MoleculeProps>((props, ref) => {
  const {
    variant,
    onClose,
    children,
    className,
    'aria-label': ariaLabel,
    ...rest
  } = props;

  const {
    props: dialogProps,
    isFullScreen,
    isFullScreenLayout,
  } = useDialogContext();

  const { dialogTitle, subtitle, headerExtras, body, footer } =
    useDialogSlots(children);

  const dialogAriaLabel = dialogTitle ? undefined : ariaLabel;

  const bodyContent = joinNodes([...headerExtras, ...body]);

  const dialogClassName = classNames(
    className,
    !isFullScreen && getWidthClassName(dialogProps),
    { [styles.fullScreenLayout]: isFullScreenLayout },
  );

  if (variant === 'alert' || variant === 'confirmation') {
    const windowedProps = {
      ...rest,
      className: dialogClassName,
      'aria-label': dialogAriaLabel,
      dialogTitle,
      body: joinNodes([
        ...(subtitle ? [subtitle] : []),
        ...headerExtras,
        ...body,
      ]),
      footer,
    };
    return variant === 'alert' ? (
      <DialogAlert {...windowedProps} ref={ref} />
    ) : (
      <DialogConfirmation {...windowedProps} ref={ref} />
    );
  }

  const fullScreenBodyClassName =
    isFullScreen && dialogProps.maxWidth
      ? classNames(
          styles.fullScreenBody,
          MAX_WIDTH_CLASS_NAMES[dialogProps.maxWidth],
        )
      : undefined;

  return (
    <DialogFullScreen
      {...rest}
      ref={ref}
      className={dialogClassName}
      aria-label={dialogAriaLabel}
      dialogTitle={dialogTitle}
      subtitle={
        isValidElement<{ children?: ReactNode }>(subtitle)
          ? subtitle.props.children
          : subtitle
      }
      body={
        fullScreenBodyClassName ? (
          <div className={fullScreenBodyClassName}>{bodyContent}</div>
        ) : (
          bodyContent
        )
      }
      onClose={onClose}
      footer={footer && <div>{footer}</div>}
    />
  );
});

Molecule.displayName = 'DialogComposite.Molecule';

export default Molecule;
